import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UpcomingMatchCard = ({ match }) => {
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const lockTime = new Date(match.predictionLockTime);
      const now = new Date();
      const diff = lockTime - now;

      if (diff <= 0) {
        setTimeRemaining('Locked');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [match?.predictionLockTime]);

  const handlePredictClick = () => {
    navigate(`/match-predictions/${match?.id}`);
  };

  const formatMatchTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isLocked = timeRemaining === 'Locked';

  return (
    <div className="bg-card rounded-lg shadow-elevation-sm overflow-hidden transition-smooth hover:shadow-elevation-md">
      <div className="p-3 md:p-4 bg-primary/5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Icon name="Trophy" size={14} color="var(--color-primary)" />
            <span className="text-xs font-medium text-primary truncate">{match?.tournament}</span>
          </div>
          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{match?.matchType}</span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 text-center min-w-0">
            <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-1.5 rounded-full overflow-hidden bg-background flex-shrink-0">
              <Image
                src={match?.team1Logo}
                alt={match?.team1LogoAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <h4 className="text-xs md:text-sm font-semibold text-foreground truncate">{match?.team1}</h4>
          </div>

          <div className="flex-shrink-0 px-2.5 py-1.5 bg-background rounded-lg">
            <span className="text-base md:text-lg font-bold text-muted-foreground">VS</span>
          </div>

          <div className="flex-1 text-center min-w-0">
            <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-1.5 rounded-full overflow-hidden bg-background flex-shrink-0">
              <Image
                src={match?.team2Logo}
                alt={match?.team2LogoAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <h4 className="text-xs md:text-sm font-semibold text-foreground truncate">{match?.team2}</h4>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between text-xs md:text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground min-w-0 flex-1">
              <Icon name="Calendar" size={14} className="flex-shrink-0" />
              <span className="break-words">{formatMatchTime(match?.matchTime)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs md:text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground min-w-0 flex-1">
              <Icon name="MapPin" size={14} className="flex-shrink-0" />
              <span className="truncate">{match?.venue}</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Icon name="Clock" size={14} color={isLocked ? 'var(--color-error)' : 'var(--color-warning)'} className="flex-shrink-0" />
              <span className="text-xs font-medium text-muted-foreground truncate">
                {isLocked ? 'Predictions Locked' : 'Lock in'}
              </span>
            </div>
            <span className={`text-xs md:text-sm font-bold flex-shrink-0 ml-2 ${isLocked ? 'text-error' : 'text-warning'}`}>
              {timeRemaining}
            </span>
          </div>
        </div>

        {match?.hasPredicted ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-1.5 py-2 bg-success/10 rounded-lg text-success">
              <Icon name="CheckCircle" size={16} />
              <span className="text-xs md:text-sm font-medium">Prediction Submitted</span>
            </div>
            <Button
              variant="outline"
              fullWidth
              iconName="Eye"
              iconPosition="left"
              onClick={handlePredictClick}
            >
              View/Edit Prediction
            </Button>
          </div>
        ) : (
          <Button
            variant="default"
            fullWidth
            iconName="Target"
            iconPosition="left"
            disabled={isLocked}
            onClick={handlePredictClick}
          >
            {isLocked ? 'Locked' : 'Make Prediction'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default UpcomingMatchCard;